/**
 * Losing a job — docs/BUSINESS-RULES.md.
 *
 * A lost job is not a deleted job. It keeps its contact, its estimate, its
 * money and its history; it simply stops appearing on the boards people work
 * from. Any job can be lost, including one already approved and in production —
 * customers do change their minds after signing, and pretending otherwise just
 * means someone deletes the row instead.
 *
 * The reason is the whole point. It is what the reporting is built on, so it is
 * required, and it comes from the managed list rather than being retyped.
 */

import { holdsRole } from "./accessRoles.js";
import type { Job, JobStage } from "./types.js";

/** Stages that mean the job is over, one way or the other. */
export const CLOSED_STAGES: readonly JobStage[] = ["closed", "lost"];

export const isLost = (job: Pick<Job, "stage">): boolean =>
  job.stage === "lost";

/**
 * Can this job be marked lost?
 *
 * Only "it is already lost" stops it. A signed or in-production job losing is
 * unusual and worth a second look from a person, but the app is not the right
 * place to argue about it.
 */
export const canMarkLost = (job: Pick<Job, "stage">): boolean => !isLost(job);

export interface LostInput {
  /** A name from the `lost_reasons` list. Required. */
  reason: string;
  note?: string | null;
}

export interface LostPatch {
  stage: "lost";
  stage_manual: true;
  lost_reason: string;
  lost_note: string | null;
  lost_at: string;
  lost_by: string | null;
}

/**
 * What to write when a job is lost.
 *
 * `stage_manual` goes on deliberately: without it the checklist would compute
 * the stage straight back to whatever the ticks say, and the job would reappear
 * on the board minutes later.
 */
export function lostPatch(
  input: LostInput,
  by: string | null,
  now: Date = new Date(),
): LostPatch {
  const reason = input.reason.trim();
  if (!reason) throw new Error("A lost reason is required.");
  return {
    stage: "lost",
    stage_manual: true,
    lost_reason: reason,
    lost_note: input.note?.trim() || null,
    lost_at: now.toISOString(),
    lost_by: by,
  };
}

export interface ReopenPatch {
  stage_manual: false;
  lost_reason: null;
  lost_note: null;
  lost_at: null;
  lost_by: null;
}

/**
 * What to write when a lost job comes back.
 *
 * The reason is cleared rather than kept, so the lost report counts jobs that
 * are lost now, not jobs that were once written off. Handing the stage back to
 * `stage_manual: false` lets the checklist work out where the job actually is.
 */
export function reopenPatch(): ReopenPatch {
  return {
    stage_manual: false,
    lost_reason: null,
    lost_note: null,
    lost_at: null,
    lost_by: null,
  };
}

/** How many of a contact's jobs were written off — shown on their page. */
export const countLost = (jobs: readonly Pick<Job, "stage">[]): number =>
  jobs.filter(isLost).length;

/**
 * A customer's jobs that are still going — neither closed nor lost.
 *
 * Asked before a rep adds another job to a customer who already has one on the
 * go: Jonathon, 2026-10-06, wants the rep asked "are you sure?" first, because
 * a second job for the same work splits its notes, its estimate and its money
 * across two rows (ADR 0272). It asks; it never refuses.
 */
export const activeJobsFor = <J extends Pick<Job, "stage" | "contact_id">>(
  jobs: readonly J[],
  contactId: string,
): J[] =>
  jobs.filter(
    (job) => job.contact_id === contactId && !CLOSED_STAGES.includes(job.stage),
  );

/**
 * A practice or demonstration job.
 *
 * Two people build this software and one of them sells roofs with it, so the
 * production database is also the demonstration database. Walking a lead
 * through end to end is the only way to find what is wrong with the screens,
 * and every walk was leaving a fake sale in the numbers.
 *
 * Jonathon, 2026-08-13: "there needs to be a test lead toggle at the job level
 * that we can turn on and won't affect our actual sold numbers or data."
 *
 * So a test job behaves exactly like a real one everywhere except where money
 * is counted. To the people who hold `tester` it is not hidden — a test lead
 * nobody can find is a test lead nobody cleans up — it is badged, and left out
 * of the totals. To everyone else the database does not return it at all
 * (ADR 0151).
 */
export const isTestJob = (job: { is_test?: boolean | null }): boolean =>
  job.is_test === true;

/**
 * The role that may see a Test job, and so may mark one.
 *
 * Row level security returns a Test job, and everything under it, only to a
 * token carrying this role (ADR 0151). Any admin may grant it; today Josh,
 * Jonathon and the e2e login hold it.
 */
export const TESTER_ROLE = "tester";

/**
 * May this caller mark a job as Test?
 *
 * The database decides; this only stops the screen offering a toggle it would
 * refuse. Somebody without the role who could turn a job into Test would lose
 * it from their own lists with no way back.
 */
export const mayMarkTest = (roles: readonly string[]): boolean =>
  holdsRole(roles, TESTER_ROLE);

/**
 * Does this job count as real work?
 *
 * The one question every list of money asks: the sold list, the payout board,
 * a contact's lifetime value. A test job is not real work and a lost one is no
 * longer work at all.
 */
export const countsAsRealWork = (
  job: Pick<Job, "stage"> & { is_test?: boolean | null },
): boolean => !isTestJob(job) && !isLost(job);
